import { useState } from "react";

import { useInsightsSearch } from "../../api/insights-search.js";
import {
  useCaptureDecision,
  useCaptureFact,
  useCaptureNote,
  useCaptureQuestion,
} from "../../api/subject-associations.js";
import { Button } from "../ui/button.js";
import { Dialog } from "../ui/dialog.js";
import { Input } from "../ui/input.js";

type CaptureKind = "fact" | "question" | "decision" | "note";

export function CaptureModal({
  open,
  subjectId,
  onClose,
}: {
  open: boolean;
  subjectId: string | null;
  onClose: () => void;
}) {
  const [kind, setKind] = useState<CaptureKind>("fact");
  const [pickedSubjectId, setPickedSubjectId] = useState(subjectId);
  const [subjectQuery, setSubjectQuery] = useState("");
  const search = useInsightsSearch(subjectQuery, {
    enabled: open && !pickedSubjectId && subjectQuery.trim().length > 0,
  });
  const activeSubjectId = pickedSubjectId ?? subjectId;

  return (
    <Dialog open={open} onClose={onClose} title="Capture">
      <div className="space-y-4">
        {!activeSubjectId ? (
          <SubjectPicker
            query={subjectQuery}
            onQuery={setSubjectQuery}
            grouped={search.grouped}
            onPick={(id) => setPickedSubjectId(id)}
          />
        ) : (
          <>
            <div className="flex flex-wrap gap-1">
              {(
                [
                  ["fact", "Fact"],
                  ["question", "Question"],
                  ["decision", "Decision"],
                  ["note", "Note"],
                ] as const
              ).map(([item, label]) => (
                <Button
                  key={item}
                  size="sm"
                  variant={kind === item ? "primary" : "secondary"}
                  onClick={() => setKind(item)}
                >
                  {label}
                </Button>
              ))}
            </div>
            {kind === "fact" ? <FactForm subjectId={activeSubjectId} onDone={onClose} /> : null}
            {kind === "question" ? (
              <QuestionForm subjectId={activeSubjectId} onDone={onClose} />
            ) : null}
            {kind === "decision" ? (
              <DecisionForm subjectId={activeSubjectId} onDone={onClose} />
            ) : null}
            {kind === "note" ? <NoteForm subjectId={activeSubjectId} onDone={onClose} /> : null}
          </>
        )}
      </div>
    </Dialog>
  );
}

function SubjectPicker({
  query,
  onQuery,
  grouped,
  onPick,
}: {
  query: string;
  onQuery: (value: string) => void;
  grouped: ReturnType<typeof useInsightsSearch>["grouped"];
  onPick: (subjectId: string) => void;
}) {
  const subjects = grouped.find((g) => g.group === "subjects")?.items ?? [];
  return (
    <div className="space-y-3">
      <p className="text-sm text-fg-muted">Pick a Subject to capture against.</p>
      <Input
        aria-label="Find a knowledge topic"
        value={query}
        onChange={(e) => onQuery(e.target.value)}
        placeholder="Find a knowledge topic…"
      />
      <ul className="max-h-56 space-y-1 overflow-y-auto">
        {subjects.map((item) =>
          item.subjectId ? (
            <li key={item.id}>
              <button
                type="button"
                className="w-full rounded-md px-2 py-2 text-left text-sm hover:bg-surface-muted"
                onClick={() => onPick(item.subjectId!)}
              >
                {item.title}
              </button>
            </li>
          ) : null,
        )}
      </ul>
    </div>
  );
}

function FactForm({ subjectId, onDone }: { subjectId: string; onDone: () => void }) {
  const mutation = useCaptureFact(subjectId);
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const [source, setSource] = useState("");
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate(
          { key, value, source, asOf, unit: null, note: null },
          { onSuccess: onDone },
        );
      }}
    >
      <Input
        aria-label="Fact key"
        placeholder="key (lower_snake_case)"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        required
      />
      <Input
        aria-label="Fact value"
        placeholder="Value"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        required
      />
      <Input
        aria-label="Source"
        placeholder="Source"
        value={source}
        onChange={(e) => setSource(e.target.value)}
        required
      />
      <Input
        aria-label="As of"
        type="date"
        value={asOf}
        onChange={(e) => setAsOf(e.target.value)}
        required
      />
      <Button type="submit" disabled={mutation.isPending}>
        Save fact
      </Button>
    </form>
  );
}

function QuestionForm({ subjectId, onDone }: { subjectId: string; onDone: () => void }) {
  const mutation = useCaptureQuestion(subjectId);
  const [text, setText] = useState("");
  const [summary, setSummary] = useState("");
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate({ text, summary: summary || undefined }, { onSuccess: onDone });
      }}
    >
      <p className="text-xs text-fg-subtle">
        Stored as an open-question node that <span className="font-semibold">questions</span> this
        Subject.
      </p>
      <Input
        aria-label="Question"
        placeholder="Open question"
        value={text}
        onChange={(e) => setText(e.target.value)}
        required
      />
      <textarea
        aria-label="Question summary"
        className="w-full rounded-md border border-border bg-surface p-2 text-sm"
        rows={3}
        placeholder="Optional detail"
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
      />
      <Button type="submit" disabled={mutation.isPending}>
        Save question
      </Button>
    </form>
  );
}

function DecisionForm({ subjectId, onDone }: { subjectId: string; onDone: () => void }) {
  const mutation = useCaptureDecision(subjectId);
  const [text, setText] = useState("");
  const [detail, setDetail] = useState("");
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate({ text, detail: detail || undefined }, { onSuccess: onDone });
      }}
    >
      <Input
        aria-label="Decision"
        placeholder="Decision"
        value={text}
        onChange={(e) => setText(e.target.value)}
        required
      />
      <textarea
        aria-label="Decision detail"
        className="w-full rounded-md border border-border bg-surface p-2 text-sm"
        rows={3}
        placeholder="Optional rationale"
        value={detail}
        onChange={(e) => setDetail(e.target.value)}
      />
      <Button type="submit" disabled={mutation.isPending}>
        Save decision
      </Button>
    </form>
  );
}

function NoteForm({ subjectId, onDone }: { subjectId: string; onDone: () => void }) {
  const mutation = useCaptureNote(subjectId);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate({ title, body }, { onSuccess: onDone });
      }}
    >
      <Input
        aria-label="Note title"
        placeholder="Note title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        required
      />
      <textarea
        aria-label="Note body"
        className="w-full rounded-md border border-border bg-surface p-2 text-sm"
        rows={5}
        placeholder="Optional body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />
      <Button type="submit" disabled={mutation.isPending}>
        Save note
      </Button>
    </form>
  );
}
